import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store';
import { resetAgree, showCollect, showUse } from '../common/popup/popupSlice';
import { postProject } from './contactSlice';
import {
  AgreeLabel,
  AgreeList,
  AgreeRow,
  Chip,
  ChipRow,
  FieldGrid,
  FieldLabel,
  FieldTitle,
  FileButton,
  FileHelp,
  FileMessage,
  FormRoot,
  HelpText,
  HiddenFileInput,
  InputLabel,
  Required,
  SelectField,
  SubmitButton,
  SubmitMessage,
  SubmitRow,
  TextArea,
  TextField,
  ViewButton,
} from './contactFormStyles';

const CATEGORIES = [
  { code: 'C1701', label: 'Business' },
  { code: 'C1702', label: 'Web Service' },
  { code: 'C1703', label: 'APP Service' },
  { code: 'C1704', label: 'VR, AR, 메타버스' },
  { code: 'C1705', label: 'VR 관리 솔루션' },
  { code: 'C1706', label: '스트리밍 플랫폼' },
  { code: 'C1707', label: '기타' },
];

const SCHEDULES = [
  { code: 'C1800', label: '계획한 일정을 알려주세요.' },
  { code: 'C1801', label: '3개월 이내' },
  { code: 'C1802', label: '3개월 ~ 6개월' },
  { code: 'C1803', label: '6개월 이상' },
  { code: 'C1804', label: '기타 (협의필요)' },
];

const ALLOWED_EXT = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'png', 'jpg', 'jpeg', 'zip'];
const MAX_SIZE = 20 * 1024 * 1024;

const EMAIL_REGEX = /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/;

const INITIAL_FIELD = {
  isSelected: '',
  textVal: '',
  scheduleVal: 'C1800',
  companyVal: '',
  nameVal: '',
  numberVal: '',
  emailVal: '',
  fileVal: null,
  agrUse: '',
  agrCollect: '',
};

// 프로젝트 의뢰 — POST /api/contact/project
export default function ProjectContact() {
  const dispatch = useAppDispatch();
  const popup = useSelector(state => state.popup);
  const fileInputRef = useRef(null);

  const [field, setField] = useState(INITIAL_FIELD);
  const [phoneNum, setPhoneNum] = useState('');
  const [fileMessage, setFileMessage] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [disabledSubmit, setDisabledSubmit] = useState(false);

  // 동의 팝업에서 '동의'를 누르면 동의 전문 + 동의 일시가 popup 스토어에 담깁니다.
  useEffect(() => {
    setField(prev => ({ ...prev, agrUse: popup.useContent, agrCollect: popup.collectContent }));
  }, [popup]);

  const onSelectCategory = code => {
    setField(prev => ({ ...prev, isSelected: code }));
    setSubmitMessage('');
  };

  const onChangeText = e => setField(prev => ({ ...prev, textVal: e.target.value }));
  const onChangeSchedule = e => setField(prev => ({ ...prev, scheduleVal: e.target.value }));
  const onChangeCompany = e => setField(prev => ({ ...prev, companyVal: e.target.value }));
  const onChangeName = e => setField(prev => ({ ...prev, nameVal: e.target.value }));
  const onChangeEmail = e => setField(prev => ({ ...prev, emailVal: e.target.value }));

  const onChangeNumber = e => {
    const val = e.target.value.substr(0, 13);

    setPhoneNum(
      val
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3')
        .replace(/(-{1,2})$/g, '')
    );
    setField(prev => ({ ...prev, numberVal: val.replace(/[^0-9]/g, '') }));
  };

  const fileUpload = e => {
    const file = e.target.files && e.target.files[0];

    if (!file) {
      setField(prev => ({ ...prev, fileVal: null }));
      setFileMessage('');
      return;
    }

    if (file.size > MAX_SIZE) {
      e.target.value = '';
      setField(prev => ({ ...prev, fileVal: null }));
      setFileMessage('파일 크기는 20MB 이하여야 합니다.');
      return;
    }

    const ext = file.name.split('.').pop().toLowerCase();
    if (ALLOWED_EXT.indexOf(ext) === -1) {
      e.target.value = '';
      setField(prev => ({ ...prev, fileVal: null }));
      setFileMessage('업로드 가능한 확장자는 doc, docx, xls, xlsx, ppt, pptx, pdf, png, jpg, jpeg, zip 입니다.');
      return;
    }

    setField(prev => ({ ...prev, fileVal: file }));
    setFileMessage(`${file.name} 첨부됨`);
  };

  const onClickUsePopup = () => dispatch(showUse());
  const onClickCollectPopup = () => dispatch(showCollect());

  const validate = () => {
    const { isSelected, textVal, scheduleVal, companyVal, nameVal, numberVal, emailVal, agrUse, agrCollect } = field;

    if (!isSelected) return '문의유형을 선택해주세요.';
    if (textVal.trim() === '') return '문의 내용을 입력해주세요.';
    if (scheduleVal === 'C1800') return '예상 일정을 선택해주세요.';
    if (companyVal.trim() === '') return '회사명을 입력해주세요.';
    if (nameVal.trim() === '') return '담당자명을 입력해주세요.';
    if (numberVal === '') return '연락처를 입력해주세요.';
    if (numberVal.length < 9 || numberVal.length > 11) return '연락처 형식이 올바르지 않습니다.';
    if (emailVal === '') return '이메일을 입력해주세요.';
    if (!EMAIL_REGEX.test(emailVal)) return '이메일 형식이 올바르지 않습니다.';
    if (agrUse === '' || agrCollect === '') return '개인정보 관련 동의를 모두 확인해주세요.';

    return '';
  };

  const onSubmit = e => {
    e.preventDefault();

    const error = validate();
    if (error) {
      setSubmitMessage(error);
      return;
    }

    // 서버는 multipart/form-data 를 받습니다. (contactSlice.postProject)
    const formData = {
      prjCateCd: field.isSelected,
      inqCont: field.textVal,
      prjOpenSchlCd: field.scheduleVal,
      companyNm: field.companyVal,
      inqerNm: field.nameVal,
      inqerPhoneNo: field.numberVal,
      inqerEmailAddr: field.emailVal,
      file: field.fileVal,
      prsInfo3cProAgrCont: field.agrUse,
      prsInfoCltAgrCont: field.agrCollect,
    };

    setDisabledSubmit(true);
    setSubmitMessage('전송 중…');

    Promise.resolve(dispatch(postProject(formData))).then(resolved => {
      setDisabledSubmit(false);

      if (resolved.type.endsWith('fulfilled')) {
        setField(INITIAL_FIELD);
        setPhoneNum('');
        setFileMessage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        dispatch(resetAgree());
        setSubmitMessage('문의가 정상적으로 접수되었습니다. 담당자가 확인 후 연락드리겠습니다.');
      } else {
        setSubmitMessage('오류가 발생하여 정상적으로 접수되지 않았습니다. info@br-it.co.kr 로 보내주세요.');
      }
    });
  };

  return (
    <FormRoot onSubmit={onSubmit} noValidate>
      <div>
        <FieldTitle>
          <Required>*</Required> 1. 문의유형 1가지를 선택해주세요.
        </FieldTitle>
        <ChipRow>
          {CATEGORIES.map(item => (
            <Chip
              key={item.code}
              type="button"
              aria-pressed={field.isSelected === item.code}
              $on={field.isSelected === item.code}
              onClick={() => onSelectCategory(item.code)}
            >
              {item.label}
            </Chip>
          ))}
        </ChipRow>
      </div>

      <div>
        <FieldLabel htmlFor="projectText">
          <Required>*</Required> 2. 내용을 자세히 작성해주시면 빠른 답변에 도움이 됩니다.
        </FieldLabel>
        <HelpText>선호하는 레퍼런스, 프로젝트 목적, 웹사이트 방향성, 문의 내용 등을 자세히 입력해주세요.</HelpText>
        <TextArea id="projectText" name="message" rows="6" spellCheck="false" value={field.textVal} onChange={onChangeText} />
      </div>

      <FieldGrid>
        <div>
          <InputLabel htmlFor="projectSchedule">
            <Required>*</Required> 예상 일정
          </InputLabel>
          <SelectField id="projectSchedule" value={field.scheduleVal} onChange={onChangeSchedule}>
            {SCHEDULES.map(item => (
              <option key={item.code} value={item.code}>
                {item.label}
              </option>
            ))}
          </SelectField>
        </div>
        <div>
          <InputLabel htmlFor="projectCompany">
            <Required>*</Required> 회사명
          </InputLabel>
          <TextField
            id="projectCompany"
            type="text"
            placeholder="회사 또는 소속을 입력해주세요."
            value={field.companyVal}
            onChange={onChangeCompany}
          />
        </div>
        <div>
          <InputLabel htmlFor="projectName">
            <Required>*</Required> 담당자명
          </InputLabel>
          <TextField
            id="projectName"
            type="text"
            placeholder="담당자 및 본인 성함을 입력해주세요."
            value={field.nameVal}
            onChange={onChangeName}
          />
        </div>
        <div>
          <InputLabel htmlFor="projectTel">
            <Required>*</Required> 연락처
          </InputLabel>
          <TextField
            id="projectTel"
            type="tel"
            placeholder="연락 가능한 연락처를 입력해주세요."
            value={phoneNum}
            onChange={onChangeNumber}
          />
        </div>
        <div>
          <InputLabel htmlFor="projectEmail">
            <Required>*</Required> 이메일
          </InputLabel>
          <TextField
            id="projectEmail"
            type="text"
            placeholder="이메일 주소를 입력해주세요."
            value={field.emailVal}
            onChange={onChangeEmail}
          />
        </div>
      </FieldGrid>

      <div>
        <FileButton htmlFor="projectFile">파일 업로드</FileButton>
        <HiddenFileInput ref={fileInputRef} id="projectFile" name="file" type="file" onChange={fileUpload} />
        <FileHelp>
          (선택) RFP 및 참고문서를 첨부해 주세요. 최대 20MB / 2개 이상은 zip으로 압축해 올려주세요.
        </FileHelp>
        {fileMessage && <FileMessage>{fileMessage}</FileMessage>}
      </div>

      <AgreeList>
        <AgreeRow>
          <AgreeLabel htmlFor="check1">
            <input id="check1" type="checkbox" name="agree" checked={popup.useAgree} onClick={onClickUsePopup} readOnly />
            <span>
              개인정보 수집 · 이용 동의 <Required>(필수)</Required>
            </span>
          </AgreeLabel>
          <ViewButton type="button" onClick={onClickUsePopup}>
            내용 보기
          </ViewButton>
        </AgreeRow>
        <AgreeRow>
          <AgreeLabel htmlFor="check2">
            <input
              id="check2"
              type="checkbox"
              name="agree"
              checked={popup.collectAgree}
              onClick={onClickCollectPopup}
              readOnly
            />
            <span>
              개인정보 취급위탁에 대한 동의 <Required>(필수)</Required>
            </span>
          </AgreeLabel>
          <ViewButton type="button" onClick={onClickCollectPopup}>
            내용 보기
          </ViewButton>
        </AgreeRow>
      </AgreeList>

      <SubmitRow>
        {submitMessage && <SubmitMessage role="status">{submitMessage}</SubmitMessage>}
        <SubmitButton type="submit" disabled={disabledSubmit}>
          &lt;&#47;Send&gt;
        </SubmitButton>
      </SubmitRow>
    </FormRoot>
  );
}
